import React from "react"
import { Accordion, Dropdown, ListGroup, ListGroupItem, Spinner } from "react-bootstrap"
import { Geo, Pencil, Plus, ThreeDotsVertical, Trash } from "react-bootstrap-icons"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import type { FLEETS } from "@src/settings/fleets/types"
import type { FENCE } from "@src/settings/geofence/types"

export type FleetAccordionItemProps = {
  fleet: FLEETS
  partnerUuid: string
  fencesState?: { loading: boolean; items: FENCE[] }
  onDeleteFence: (fleetUuid: string, fenceId: number) => Promise<void>
}

export const FleetAccordionItem: React.FC<FleetAccordionItemProps> = ({
  fleet,
  partnerUuid,
  fencesState,
  onDeleteFence,
}) => {
  const { t } = useTranslation()
  return (
    <Accordion.Item eventKey={fleet.uuid} key={fleet.uuid}>
      <Accordion.Header>
        <div className="d-flex align-items-center w-100 justify-content-between">
          <span>{fleet.name ?? "-"}</span>
          {fencesState?.loading && (
            <span className="accordion-caret-spinner me-2">
              <Spinner animation="border" size="sm" />
            </span>
          )}
        </div>
      </Accordion.Header>
      <Accordion.Body className="py-1 px-0">
        {fencesState?.loading ? (
          <div className="d-flex justify-content-center mt-2">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <ListGroup variant="flush" className="mt-0">
            <ListGroup.Item
              action
              as={Link}
              to={`/${partnerUuid}/settings/geofences/${fleet.uuid}/create?fleetName=${encodeURIComponent(
                fleet.name ?? "-",
              )}`}
            >
              <span className="ms-3 d-inline-flex align-items-center">
                <Plus size={14} className="me-3" />
                {t("settings.fence.new_fence")}
              </span>
            </ListGroup.Item>
            {fencesState?.items?.length ? (
              fencesState.items.map((fence) => (
                <ListGroupItem key={fence.id}>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="ms-3 d-inline-flex align-items-center">
                      <Geo size={14} className="me-3" />
                      {fence.name ?? "-"}
                    </span>
                    <Dropdown align="end">
                      <Dropdown.Toggle variant="link" className="p-0 text-muted" bsPrefix="btn">
                        <ThreeDotsVertical size={16} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          as={Link}
                          to={`/${partnerUuid}/settings/geofences/${fleet.uuid}/${fence.id}/edit?fleetName=${encodeURIComponent(
                            fleet.name ?? "-",
                          )}`}
                        >
                          <Pencil size={14} className="me-2" /> {t("common.edit")}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={async (e) => {
                            e.preventDefault()
                            await onDeleteFence(fleet.uuid, fence.id)
                          }}
                          className="text-danger"
                        >
                          <Trash size={14} className="me-2" /> {t("settings.fleets.delete")}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </ListGroupItem>
              ))
            ) : (
              <ListGroup.Item className="text-muted">
                <span className="ms-3">{t("settings.fence.no_fence")}</span>
              </ListGroup.Item>
            )}
          </ListGroup>
        )}
      </Accordion.Body>
    </Accordion.Item>
  )
}

export default FleetAccordionItem
